import { useTranslation } from 'react-i18next';
import { TagSection } from '../../TagSection';
import { usePlatformCurrencyCode } from '@/clients/platformInfo/usePlatformInfo.ts';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import {
    PRODUCT_CATEGORIES,
    PRODUCT_CATEGORY,
    PRODUCT_GROUP_TYPE,
    PRODUCT_GROUP_TYPES,
    SUPPORTED_UNITS,
} from '@schuettflix/interfaces';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';

export interface FrancoIndicatorProductProps {
    category: PRODUCT_CATEGORY;
    amount: number;
    salesPrice: number;
    unit: SUPPORTED_UNITS;
}

export type FrancoIndicatorProps = {
    products: FrancoIndicatorProductProps[];
    productGroupType?: PRODUCT_GROUP_TYPE;
};

export function FrancoIndicator({ products, productGroupType }: FrancoIndicatorProps) {
    const currencyCode = usePlatformCurrencyCode();
    const { t } = useTranslation();
    const { formatNumberWithCurrencyPerUnit } = useFormatters();

    // franco indicator is only relevant for franco and merchant product groups
    if (
        !productGroupType ||
        (productGroupType !== PRODUCT_GROUP_TYPES.FRANCO && productGroupType !== PRODUCT_GROUP_TYPES.MERCHANT)
    ) {
        return null;
    }

    const relevantProducts = products.filter(product =>
        ([PRODUCT_CATEGORIES.MATERIAL, PRODUCT_CATEGORIES.TRANSPORT] as PRODUCT_CATEGORY[]).includes(product.category)
    );
    const leadingProduct = relevantProducts.find(product => product.category === 'MATERIAL');

    if (!leadingProduct || !relevantProducts.length) {
        return null;
    }

    const totalSalesPrice = relevantProducts.reduce((total, product) => {
        return total + product.salesPrice;
    }, 0);

    const amountLabel = `${leadingProduct.amount} ${t(SUPPORTED_UNIT_LABEL[leadingProduct.unit])}`;
    const pricePerUnitLabel = formatNumberWithCurrencyPerUnit(totalSalesPrice, leadingProduct.unit, currencyCode);

    return (
        <TagSection
            title={t('product.balanceSheet.francoIndicator')}
            description={amountLabel}
            label={pricePerUnitLabel}
        />
    );
}
